import { Close } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Box, IconButton, Typography } from '@mui/material'
import React from 'react'
import { colors, theme } from '../theme'
import { StripeConnectButton } from './StripeConnectButton'

interface Props {
  isAdmin: boolean
  onClose?: () => void
  onRefresh?: () => void
}

const SectionTitle: React.FC<{ index: number; title: string }> = ({ index, title }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        border: `3px solid ${theme.palette.primary.main}`,
        color: theme.palette.primary.main,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: 14,
        flexShrink: 0,
      }}
    >
      {index}
    </Box>
    <Typography sx={{ fontWeight: 700, color: colors.cello, fontSize: 18 }}>{title}</Typography>
  </Box>
)

const REFRESH_KEY = 'stripe-connect-attempted'

export const StripeConnectRequiredStep: React.FC<Props> = ({ isAdmin, onClose, onRefresh }) => {
  const [hasTriedConnect, setHasTriedConnect] = React.useState<boolean>(
    () => window.sessionStorage.getItem(REFRESH_KEY) === 'true'
  )

  const handleConnectAttempt = () => {
    setHasTriedConnect(true)
    window.sessionStorage.setItem(REFRESH_KEY, 'true')
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '1000px', position: 'relative' }}>
      {onClose && (
        <IconButton onClick={onClose} sx={{ position: 'absolute', top: 8, right: 8 }}>
          <Close />
        </IconButton>
      )}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontWeight: 800, color: colors.blueSky, fontSize: { xs: 22, md: 28 } }}>
          {isAdmin ? '🐶 Action requise' : "🐶 Action d'un administrateur requise"}
        </Typography>
        <Typography sx={{ fontWeight: 700, color: colors.blueSky, fontSize: { xs: 18, md: 24 } }}>
          Activer le paiement adoptant en ligne
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 3, md: 4 },
        }}
      >
        <Box sx={{ flex: 1 }}>
          <SectionTitle index={1} title="Activer votre compte Stripe connect x Petso" />
          <Typography sx={{ color: colors.blueSky, mb: 2, fontSize: { xs: 14, md: 16 } }}>
            Nous travaillons en partenariat technique avec Stripe, entreprise mondialement reconnue qui assure
            l&apos;ensemble des opérations financières et de réception des frais d&apos;adoption en ligne.
          </Typography>
          <Typography sx={{ color: colors.blueSky, mb: 3, fontSize: { xs: 14, md: 16 } }}>
            Pour des raisons de réglementation européenne et pour vous permettre de recevoir la perception des frais
            d&apos;adoption, merci de compléter ce court formulaire.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
            <StripeConnectButton
              variant="contained"
              color="primary"
              fullWidth={false}
              content="Continuer"
              onConnectAttempt={handleConnectAttempt}
              sx={{
                textTransform: 'none',
                borderRadius: '999px',
                px: 6,
                py: 1.5,
                fontWeight: 700,
                backgroundColor: theme.palette.primary.main,
                color: colors.white,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                },
              }}
            />
            {hasTriedConnect && onRefresh && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography sx={{ color: colors.blueSky, fontSize: { xs: 12, md: 13 }, fontWeight: 600 }}>
                  Déjà fait&nbsp;?
                </Typography>
                <LoadingButton
                  variant="outlined"
                  onClick={onRefresh}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '999px',
                    px: 6,
                    py: 1.5,
                    fontWeight: 700,
                    color: colors.cello,
                    borderColor: colors.cello,
                  }}
                >
                  Rafraîchir le statut
                </LoadingButton>
              </Box>
            )}
          </Box>
          {!isAdmin && (
            <Typography sx={{ mt: 1.5, color: colors.blueSky, fontSize: { xs: 13, md: 14 } }}>
              Demandez &agrave; un administrateur de votre association d&apos;activer Stripe Connect pour continuer.
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            width: { xs: '100%', md: '1px' },
            height: { xs: '1px', md: 'auto' },
            backgroundColor: colors.geyser,
          }}
        />

        <Box sx={{ flex: 1 }}>
          <SectionTitle index={2} title="Pourquoi ?" />
          <Typography sx={{ color: colors.blueSky, mb: 2, fontSize: { xs: 14, md: 16 } }}>
            A la création d&apos;une procédure d&apos;adoption, le règlement des frais d&apos;adoption en ligne sera
            désormais permis.
          </Typography>
          <Box component="ul" sx={{ pl: 3, color: colors.blueSky, mb: 2, fontSize: { xs: 14, md: 16 } }}>
            <Box component="li" sx={{ mb: 1 }}>
              Un mail sera envoyé au futur adoptant, lui donnant accès à un profil dédié pour retrouver les informations
              essentielles de l&apos;animal et régler les frais d&apos;adoption en ligne.
            </Box>
            <Box component="li">
              Vous êtes notifié de la réception et bénéficiez d&apos;un espace pour suivre les réceptions.
            </Box>
          </Box>

          <SectionTitle index={3} title="C'est important pour nous 😺" />
          <Typography sx={{ color: colors.blueSky, fontSize: { xs: 14, md: 16 } }}>
            Lors du paiement, les adoptants sont en mesure de donner un pourboire facultatif à Petso afin de compenser
            les frais de paiement et la maintenance de l&apos;outil pour les associations. C&apos;est notre moyen de
            vous mettre cet outil gratuitement à disposition.
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}
